import { defineStore } from "pinia";
import { useAuthFetch } from "~/composables/useAuthFetch";
import { UserRole } from "~/types/userRole";
import { ResponseStatus } from "~/types/responseStatus";
import { sleep } from "~/utils";
import { AppEvents } from "../types/events";
import { isUserRole } from "../composables/utils";

interface UserPayloadInterface {
  username: string;
  password: string;
}

export const useAuthStore = defineStore("auth", {
  state: () => ({
    authenticated: false,
    loading: false,
    accessToken: null,
    userId: null,
    role: null,
    stripeSubscriptionInfo: null,
  }),
  actions: {
    async checkUser() {
      await useAuthFetch(`/users/${this.userId}/active`, {
        method: "PUT",
        onResponse({ response }) {},
      });

      if (isUserRole.value([UserRole.MANAGER, UserRole.SALES_REP])) {
        const { userInfo, getUserEmail } = storeToRefs(useUserStore());
        await this.checkStripeSubscription({ email: getUserEmail.value });
      }
    },
    async checkStripeSubscription({ email = "" }) {
      if (email === "") return;

      const config = useRuntimeConfig()
      const response = await $fetch(
        "/api/stripe/check-subscription-status",
        {
          headers: {
            'Access-Control-Allow-Origin': '*'
          },
          baseURL: config.public.HOST_URL,
          method: "POST",
          body: { email: email },
        }
      );

      this.stripeSubscriptionInfo = response?.subscription;

      return response;
    },
    async deleteTeamMember(teamMemberId: any) {
      const config = useRuntimeConfig();

      const managerId = this.userId;

      const { data, error, status }: any = await useAuthFetch(
        `${config.public.API_BASE_URL}/managers/${managerId}/team/${teamMemberId}`,
        {
          method: "DELETE"
        }
      );

      console.log(data, error, status);
      if (status.value === ResponseStatus.SUCCESS) {
        sleep(3000).then(() => {
          window.location.reload();
        });
      }

      return {
        status: status.value,
        data: data.value,
        error: error.value,
      };
    },
    async sendSalesRepInvitation(type: UserRole, formData: any) {
      const config = useRuntimeConfig();

      const { data, error, status }: any = await useAuthFetch(
        `${config.public.API_BASE_URL}/users/invite/sale`,
        {
          method: "POST",
          body: formData
        }
      );

      return {
        status: status.value,
        data: data.value,
        error: error.value,
      };
    },
    async sendNewsletterInvitation(formData: any) {
      const { data, error, status }: any = await useAuthFetch(
        `/newsletter/subscribe`,
        {
          method: "POST",
          body: formData,
        }
      );

      return {
        status: status.value,
        data: data.value,
        error: error.value,
      };
    },
    async confirmSubscription(formData: any) {
      const { data, error, status }: any = await useAuthFetch(
        `/newsletter/subscribe`,
        {
          method: "POST",
          body: formData,
        }
      );

      return {
        status: status.value,
        data: data.value,
        error: error.value,
      };
    },
    async addNewTag(formData) {
      const config = useRuntimeConfig();

      const { data, error, status }: any = await useAuthFetch(
        `${config.public.API_BASE_URL}/tag`,
        {
          method: "POST",
          body: formData
        }
      );

      return {
        status: status.value,
        data: data.value,
        error: error.value,
      };
    },

    async signUpUser(type: UserRole, formData: any) {
      const config = useRuntimeConfig();

      const formDataBody = new FormData();
      const formDataXwww = new URLSearchParams();

      //debugger;
      for (const key in formData) {
        if (Array.isArray(formData[key])) {
          for (const item of formData[key]) {
            formDataBody.append(key + '[]', item);
          }
          continue;
        }
        formDataBody.append(key,  formData[key]);
      }

      for (const pair of formDataBody) {
        formDataXwww.append(pair[0], String(pair[1]));
      }

      if (type === UserRole.MANAGER || type === UserRole.SALES_REP) {
        const { data, error, status }: any = await useAuthFetch(
          `${config.public.API_BASE_URL}/auth/sign-up`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
            },
            body: formDataXwww,
          }
        );

        if (data.value) {
          // po rejestracji
        }

        return {
          status: status.value,
          data: data.value,
          error: error.value,
        };
      } else {
        const { data, error, status }: any = await useAuthFetch(
          `${config.public.API_BASE_URL}/auth/sign-up`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
            },
            body: formDataXwww,
          }
        );

        if (data.value) {
          // po rejestracji
        }

        return {
          status: status.value,
          data: data.value,
          error: error.value,
        };
      }
    },
    async authenticateUser({ username, password }: UserPayloadInterface) {
      const config = useRuntimeConfig();

      const { data, pending, error, status }: any = await useAuthFetch(
        `${config.public.API_BASE_URL}/auth/sign-in`,
        {
          method: "POST",
          body: {
            username,
            password,
          },
        }
      );
      this.loading = pending;

      if (data.value) {
        const token = useCookie("token"); // useCookie new hook in nuxt 3
        token.value = data?.value?.accessToken; // set token to cookie

        if (token.value) {
          const userId = useCookie("userId");
          userId.value = data?.value?.userId;

          const userRoleCookie = useCookie("role");
          userRoleCookie.value = data?.value?.role;

          this.accessToken = token.value;
          this.userId = userId.value;
          this.authenticated = !!this.accessToken; // set authenticated  state value to true
          this.role = data?.value?.role;

          const { $toast } = useNuxtApp();

          $toast.success("Zalogowano");
          await this.checkUser();
        }
      }

      return {
        status: status.value,
        data: data.value,
        error: error.value,
      };
    },
    logoutUser() {
      const nuxt = useNuxtApp();
      const token = useCookie("token"); // useCookie new hook in nuxt 3
      const userId = useCookie("userId"); // useCookie new hook in nuxt 3
      const userRole = useCookie("role"); // useCookie new hook in nuxt 3
      this.accessToken = null;
      this.userId = null;
      this.authenticated = false; // set authenticated  state value to false
      this.role = null;

      token.value = null; // clear the token cookie
      userId.value = null; // clear the token cookie
      userRole.value = null; // clear the token cookie
      const { userInfo } = storeToRefs(useUserStore());
      userInfo.value = {};
      const { $event } = useNuxtApp();
      $event(AppEvents.RESET_CHAT_DATA);

      window.location.href = `/${nuxt.$i18n.locale.value}/marketplace`;
    },
  },
});
